// src/pages/Profile/Profile.js

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import LoadingOverlay from "../../components/LoadingOverlay/LoadingOverlay";
import ProfileInfo from "../../components/ProfileInfo/ProfileInfo";
import ProfileFilter from "../../components/ProfileFilter/ProfileFilter";
import { useAlertDialog } from "../../components/ConfirmDialog/ConfirmDialogProvider";
import Seo from "../../components/Seo/Seo";
import { buildAuthPathWithNext, getCurrentAppPath, sanitizeInternalPath } from "../../utils/authRedirect";
import { ROUTES } from "../../utils/appRoutes";
import { SUBSCRIPTIONS_ENABLED } from "../../utils/features";
import './Profile.css';

function Profile({ refreshTokens, handleLogout, user, onUserUpdate, onProfilePictureVersionUpdate, profilePictureVersion }) {
  const [userData, setUserData] = useState(user || null);
  const [userGames, setUserGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const showAlert = useAlertDialog();
  const location = useLocation();
  const navigate = useNavigate();
  const userDataRef = useRef(userData);

  // Read initial tab from query param (?tab=subscription/games/ranking)
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const tabParam = searchParams.get('tab');
  const allowedTabs = SUBSCRIPTIONS_ENABLED
    ? ['ranking', 'subscription', 'games']
    : ['ranking', 'games'];
  const initialTab = allowedTabs.includes(tabParam) ? tabParam : 'ranking';
  const checkoutStatus = searchParams.get('checkout');
  const nextAfterCheckout = sanitizeInternalPath(searchParams.get('next'));
  const loginPath = useMemo(
    () => buildAuthPathWithNext(ROUTES.LOGIN, getCurrentAppPath(location)),
    [location]
  );

  const totalAvailableCredits = (credits) =>
    Array.isArray(credits)
      ? credits.reduce((sum, entry) => sum + (Number(entry?.amount) || 0), 0)
      : 0;

  // Same as the navbar's Log out: clear the session, then go home.
  const handleLogoutClick = useCallback(() => {
    handleLogout();
    navigate(ROUTES.HOME);
  }, [handleLogout, navigate]);

  const redirectToLogin = useCallback(() => {
    handleLogout();
    navigate(loginPath, { replace: true });
  }, [handleLogout, loginPath, navigate]);

  useEffect(() => {
    if (user) {
      setUserData(user);
    }
  }, [user]);

  useEffect(() => {
    userDataRef.current = userData;
  }, [userData]);

  // Fetch user data and games securely using the idToken
  useEffect(() => {
    const fetchUserDataAndGames = async () => {
      let idToken = localStorage.getItem("idToken");
  
      if (!idToken) {
        redirectToLogin();
        return;
      }

      try {
        // Fetch user profile
        const profileResponse = await fetch(`${process.env.REACT_APP_API_URL}/profile/user-profile`, {
          method: "GET",
          headers: {
            "Authorization": `Bearer ${idToken}`,
            "Content-Type": "application/json",
          },
        });

        if (profileResponse.status === 401) {
          const tokenRefreshed = await refreshTokens();
          if (tokenRefreshed) {
            idToken = localStorage.getItem("idToken");
            if (!idToken) {
              redirectToLogin();
              return;
            }
            const retryProfileResponse = await fetch(`${process.env.REACT_APP_API_URL}/profile/user-profile`, {
              method: "GET",
              headers: {
                "Authorization": `Bearer ${idToken}`,
                "Content-Type": "application/json",
              },
            });
            if (!retryProfileResponse.ok) {
              throw new Error("Failed to fetch user data after token refresh");
            }
            const data = await retryProfileResponse.json();
            setUserData(data);
            if (onUserUpdate) onUserUpdate(data);
          } else {
            redirectToLogin();
            return;
          }
        } else if (!profileResponse.ok) {
          throw new Error("Failed to fetch user data");
        } else {
          const userData = await profileResponse.json();
          setUserData(userData);
          if (onUserUpdate) onUserUpdate(userData);
        }

        // Fetch user games
        const gamesResponse = await fetch(`${process.env.REACT_APP_API_URL}/profile/user-games`, {
          method: "GET",
          headers: {
            "Authorization": `Bearer ${idToken}`,
            "Content-Type": "application/json",
          },
        });

        if (gamesResponse.status === 401) {
          const tokenRefreshed = await refreshTokens();
          if (!tokenRefreshed) {
            redirectToLogin();
            return;
          }

          idToken = localStorage.getItem("idToken");
          if (!idToken) {
            redirectToLogin();
            return;
          }

          const retryGamesResponse = await fetch(`${process.env.REACT_APP_API_URL}/profile/user-games`, {
            method: "GET",
            headers: {
              "Authorization": `Bearer ${idToken}`,
              "Content-Type": "application/json",
            },
          });

          if (!retryGamesResponse.ok) {
            throw new Error("Failed to fetch user games after token refresh");
          }

          const gamesData = await retryGamesResponse.json();
          setUserGames(gamesData);
        } else if (!gamesResponse.ok) {
          throw new Error("Failed to fetch user games");
        } else {
          const gamesData = await gamesResponse.json();
          setUserGames(gamesData); // Set games data
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchUserDataAndGames();
  }, [refreshTokens, onUserUpdate, redirectToLogin]);

  useEffect(() => {
    const shouldAutoReturnToGame =
      checkoutStatus === 'success' &&
      typeof nextAfterCheckout === 'string' &&
      nextAfterCheckout.startsWith('/games/');

    if (!shouldAutoReturnToGame) return;

    let active = true;
    let attempts = 0;
    const maxAttempts = 20;
    const intervalMs = 2000;

    const fetchLatestProfile = async () => {
      let idToken = localStorage.getItem("idToken");
      if (!idToken) return null;

      try {
        let response = await fetch(`${process.env.REACT_APP_API_URL}/profile/user-profile`, {
          method: "GET",
          headers: {
            "Authorization": `Bearer ${idToken}`,
            "Content-Type": "application/json",
          },
        });

        if (response.status === 401) {
          const refreshed = await refreshTokens();
          if (!refreshed) return null;

          idToken = localStorage.getItem("idToken");
          response = await fetch(`${process.env.REACT_APP_API_URL}/profile/user-profile`, {
            method: "GET",
            headers: {
              "Authorization": `Bearer ${idToken}`,
              "Content-Type": "application/json",
            },
          });
        }

        if (!response.ok) return null;
        return response.json();
      } catch (_) {
        return null;
      }
    };

    const checkAndRedirect = async () => {
      if (!active) return;

      const currentCredits = totalAvailableCredits(userDataRef.current?.credits);
      if (currentCredits > 0) {
        navigate(nextAfterCheckout, { replace: true });
        return;
      }

      attempts += 1;
      const latestProfile = await fetchLatestProfile();
      if (!active) return;

      if (latestProfile) {
        setUserData(latestProfile);
        if (onUserUpdate) onUserUpdate(latestProfile);

        const latestCredits = totalAvailableCredits(latestProfile.credits);
        if (latestCredits > 0) {
          navigate(nextAfterCheckout, { replace: true });
          return;
        }
      }

      if (attempts < maxAttempts) {
        setTimeout(checkAndRedirect, intervalMs);
      }
    };

    checkAndRedirect();

    return () => {
      active = false;
    };
  }, [checkoutStatus, nextAfterCheckout, navigate, onUserUpdate, refreshTokens]);

  const handleProfilePictureUpdate = (newProfileUrl, version) => {
    setUserData((prev) => {
      const base = prev || user || {};
      const updated = { ...base, profilePictureUrl: newProfileUrl };
      if (onUserUpdate) onUserUpdate(updated);
      if (onProfilePictureVersionUpdate && version) {
        onProfilePictureVersionUpdate(version);
      }
      return updated;
    });
  };

  const handleProfileDataUpdate = (updatedUser) => {
    if (!updatedUser) return;
    setUserData(updatedUser);
    if (onUserUpdate) onUserUpdate(updatedUser);
  };

  // Function to handle quitting the waitlist
  const handleQuitWaitlist = async (gameId) => {
    const idToken = localStorage.getItem("idToken");
    if (!idToken) {
      await showAlert({
        title: "WAITLIST",
        message: "You must be logged in to perform this action.",
      });
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/games/${gameId}/waitlist`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to quit waitlist");
      }

      // Remove the game from userGames
      setUserGames((prevGames) => prevGames.filter((game) => game.gameId._id !== gameId));

      await showAlert({
        title: "WAITLIST",
        message: "You have successfully left the waitlist.",
      });
    } catch (error) {
      console.error("Error quitting waitlist:", error);
      await showAlert({
        title: "WAITLIST",
        message: "Failed to quit waitlist. Please try again.",
      });
    }
  };

  if (loading) {
    return (
      <>
        <Seo
          title="Your Profile | ZealFC"
          description="Manage your ZealFC account, profile details, and football activity."
          robots="noindex, nofollow"
          canonicalPath={ROUTES.PROFILE}
        />
        <div className="profile-container">
          <LoadingOverlay label="Loading profile" />
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Seo
          title="Your Profile | ZealFC"
          description="Manage your ZealFC account, profile details, and football activity."
          robots="noindex, nofollow"
          canonicalPath={ROUTES.PROFILE}
        />
        <div>Error: {error}</div>
      </>
    );
  }

  return (
    <>
      <Seo
        title="Your Profile | ZealFC"
        description="Manage your ZealFC account, profile details, and football activity."
        robots="noindex, nofollow"
        canonicalPath={ROUTES.PROFILE}
      />
      <div className="profile-container">
        {userData ? (
          <>
            <ProfileInfo
              userData={userData}
              onProfilePictureUpdate={handleProfilePictureUpdate}
              onProfileDataUpdate={handleProfileDataUpdate}
              profilePictureVersion={profilePictureVersion}
              onEditModeChange={setIsEditingProfile}
            />
            <div className={`profile-filter-wrap ${isEditingProfile ? "is-blurred" : ""}`}>
              <ProfileFilter
                games={userGames}
                user={userData}
                initialTab={initialTab}
                suspendStats={isEditingProfile}
                refreshTokens={refreshTokens}
                handleLogout={handleLogout}
                onQuitWaitlist={handleQuitWaitlist}
              />
            </div>

            {/* Phones lose the navbar's dropdown, which is where Log out
                lives, so it sits at the end of the profile as in the app. */}
            <div className="profile-logout">
              <button type="button" className="profile-logout-link" onClick={handleLogoutClick}>
                Log out
              </button>
            </div>
          </>
        ) : (
          <p>No user data available</p>
        )}
      </div>
    </>
  );
}

export default Profile;
